'use client';

import { useEffect, useRef, useState } from 'react';

type Parts = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  done: boolean;
};

function computeParts(remainingMs: number): Parts {
  if (remainingMs <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, done: true };
  }
  const total = Math.floor(remainingMs / 1000);
  return {
    days: Math.floor(total / 86_400),
    hours: Math.floor((total % 86_400) / 3_600),
    minutes: Math.floor((total % 3_600) / 60),
    seconds: total % 60,
    done: false,
  };
}

type CountdownProps = {
  /** Target moment, ISO 8601 with explicit offset. */
  targetIso: string;
  /** Server clock at render time, epoch ms — the trusted time base. */
  serverNow: number;
  units: readonly string[];
  /** Shown when the target is already in the past on the server. */
  doneLabel: string;
  /** Shown when the timer reaches zero live in the browser. */
  liveLabel: string;
  /** True when the server already considers this target reached. */
  alreadyDone?: boolean;
  /** Fired once, the first time the timer hits zero in the browser. */
  onZero?: () => void;
  /** `lg` (hero) or `sm` (map rail). */
  size?: 'lg' | 'sm';
  /** Border/label colour for the surface the timer sits on. */
  tone?: 'cream' | 'coal';
};

export default function Countdown({
  targetIso,
  serverNow,
  units,
  doneLabel,
  liveLabel,
  alreadyDone = false,
  onZero,
  size = 'lg',
  tone = 'cream',
}: CountdownProps) {
  const targetMs = Date.parse(targetIso);
  const firedZero = useRef(false);

  // First render (server + hydration) is computed from `serverNow` so the markup
  // matches on both sides. After mount the interval switches to the real clock.
  const [parts, setParts] = useState<Parts>(() =>
    alreadyDone ?
      { days: 0, hours: 0, minutes: 0, seconds: 0, done: true }
    : computeParts(targetMs - serverNow),
  );

  useEffect(() => {
    if (alreadyDone) return;

    const tick = () => {
      const next = computeParts(targetMs - Date.now());
      setParts(next);
      if (next.done && !firedZero.current) {
        firedZero.current = true;
        onZero?.();
      }
    };

    tick();
    const id = window.setInterval(tick, 1_000);
    return () => window.clearInterval(id);
  }, [alreadyDone, targetMs, onZero]);

  if (alreadyDone || parts.done) {
    return (
      <span
        className={`${size === 'lg' ? 'hub-label-lg' : 'hub-label'} inline-flex items-center gap-2 text-yamal`}
        role="status"
      >
        <span className="size-2 animate-pulse rounded-full bg-yamal" aria-hidden="true" />
        {alreadyDone ? doneLabel : liveLabel}
      </span>
    );
  }

  const cells = [
    { value: parts.days, label: units[0] },
    { value: parts.hours, label: units[1] },
    { value: parts.minutes, label: units[2] },
    { value: parts.seconds, label: units[3] },
  ];

  const border = tone === 'cream' ? 'border-cream/20' : 'border-coal/25';
  const unitColor = tone === 'cream' ? 'text-cream/50' : 'text-coal/55';
  const digitColor = tone === 'cream' ? 'text-cream' : 'text-coal';

  return (
    <div
      className={`flex ${size === 'lg' ? 'gap-2 sm:gap-3' : 'gap-1.5'}`}
      role="timer"
      aria-live="off"
    >
      {cells.map((cell) => (
        <div
          key={cell.label}
          className={`border text-center ${border} ${
            size === 'lg' ? 'min-w-[72px] px-3 py-3 sm:min-w-[92px]' : 'min-w-[60px] px-2 py-2.5'
          }`}
        >
          <div
            className={`hub-display tabular-nums ${digitColor} ${
              size === 'lg' ? 'text-5xl sm:text-6xl' : 'text-3xl'
            }`}
          >
            {String(cell.value).padStart(2, '0')}
          </div>
          <div
            className={`${unitColor} ${size === 'lg' ? 'hub-label-xs mt-1.5' : 'hub-label-xs mt-1'}`}
          >
            {cell.label}
          </div>
        </div>
      ))}
    </div>
  );
}
